import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFloating, offset, flip, shift, arrow, autoUpdate, useDismiss, useInteractions, useMergeRefs, FloatingFocusManager } from '@floating-ui/react';
import { InlineButton } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { MenuSVGpath } from '@skatteetaten/ds-icons';
import './styles.css';


var styles = {
    "arrow": "TopBannerInternalActionMenu-module_arrow__n102G",
    "menu": "TopBannerInternalActionMenu-module_menu__x42Gm"
};

/* eslint-disable react-compiler/react-compiler */ /* eslint-disable react/forbid-dom-props */ const TopBannerInternalActionMenu = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, menuActionsRef, children }, ref)=>{
    const arrowRef = useRef(null);
    const arrowLen = arrowRef.current?.offsetWidth ?? 0;
    // +6 for at pilen skal ligge på utsiden av outline på knappen
    const floatingOffset = Math.sqrt(2 * arrowLen ** 2) / 2 + 6;
    const [isOpen, setOpen] = useState(false);
    const floatingData = useFloating({
        open: isOpen,
        onOpenChange: (open)=>{
            setOpen(open);
        },
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({
                mainAxis: floatingOffset,
                alignmentAxis: -32
            }),
            flip(),
            shift(),
            arrow({
                element: arrowRef
            })
        ]
    });
    const dismiss = useDismiss(floatingData.context, {
        ancestorScroll: true
    });
    const interactions = useInteractions([
        dismiss
    ]);
    const { refs, floatingStyles, middlewareData } = floatingData;
    const { getFloatingProps, getReferenceProps } = interactions;
    const { t } = useTranslation('ds_layout', {
        i18n: dsI18n
    });
    const buttonRef = useRef(null);
    const mergedButtonRef = useMergeRefs([
        refs.setReference,
        buttonRef,
        ref
    ]);
    useImperativeHandle(menuActionsRef, ()=>({
            open: ()=>setOpen(true),
            close: ()=>setOpen(false)
        }));
    useEffect(()=>{
        if (buttonRef.current) {
            buttonRef.current.ariaExpanded = isOpen.toString();
        }
    }, [
        isOpen
    ]);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(InlineButton, {
                id: id,
                className: className,
                lang: lang,
                "data-testid": dataTestId,
                ...getReferenceProps(),
                ref: mergedButtonRef,
                svgPath: MenuSVGpath,
                brightness: 'light',
                onClick: ()=>{
                    setOpen(!isOpen);
                },
                children: t('topbannerbutton.Menu')
            }),
            isOpen && /*#__PURE__*/ jsx(FloatingFocusManager, {
                returnFocus: buttonRef,
                context: floatingData.context,
                modal: false,
                // -1 her for å hindre at fokusManager overstyrer fokus vekk fra knappen når menyen åpnes
                initialFocus: -1,
                children: /*#__PURE__*/ jsxs("div", {
                    className: styles.menu,
                    ...getFloatingProps(),
                    ref: refs.setFloating,
                    style: floatingStyles,
                    children: [
                        children,
                        /*#__PURE__*/ jsx("div", {
                            ref: arrowRef,
                            style: {
                                left: middlewareData.arrow?.x,
                                top: `-${(arrowRef.current?.offsetWidth ?? 0) / 2}px`
                            },
                            className: `${styles.arrow}`.trim()
                        })
                    ]
                })
            })
        ]
    });
});

export { TopBannerInternalActionMenu };
